import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, UserPlus, X } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CountryPicker } from "@/components/travel/CountryPicker";
import { PrivateNote } from "@/components/contacts/PrivateNote";
import { MyCode } from "@/components/swap/MyCode";
import { ScanCode } from "@/components/swap/ScanCode";
import { NfcSticker } from "@/components/swap/NfcSticker";
import { PrintCard } from "@/components/swap/PrintCard";
import { PeopleFinder } from "@/components/friends/PeopleFinder";
import { PhoneBookImport } from "@/components/contacts/PhoneBookImport";
import { DockSheet } from "./DockSheet";

const blank = { name: "", profession: "", phone: "", whatsapp: "", email: "", instagram: "", linkedin: "", tiktok: "", notes: "" };

const ContactForm = ({ names, onDone }) => {
  const qc = useQueryClient();
  const newPlace = () => ({ id: Math.random().toString(36).slice(2), country: "", city: "" });
  const [places, setPlaces] = useState(() => [newPlace()]);
  const [f, setF] = useState(blank);
  const setPlace = (i, v) => setPlaces(places.map((p, j) => (j === i ? { ...p, ...v } : p)));
  const [busy, setBusy] = useState(false);
  const field = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }) });
  const submit = async (e) => {
    e.preventDefault();
    const chosen = places.filter((p) => p.country);
    if (!chosen.length) return toast.error("Pick the country they're in");
    setBusy(true);
    const done = [];
    try {
      // Same person is saved under every place they live
      for (const p of chosen) done.push((await api.addContact({ country: p.country, city: p.city, contact: { ...blank, ...f, details: "" } })).country);
      toast.success(`${f.name || "Contact"} saved under ${done.join(", ")}`);
      setF(blank); setPlaces([newPlace()]);
      onDone();
    } catch (err) {
      const saved = done.length ? ` (saved under ${done.join(", ")} only)` : "";
      toast.error(`${err?.response?.data?.detail || "Couldn't save the contact"}${saved}`);
    }
    qc.invalidateQueries({ queryKey: ["state"] });
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-2 mt-4" data-testid="add-contact-form">
      <p className="col-span-2 text-xs text-slate-400">Where do they live?</p>
      {places.map((p, i) => (
        <div key={p.id} className="col-span-2 flex items-center gap-2" data-testid={`add-contact-place-${i}`}>
          <CountryPicker names={names} exclude={places.map((x) => x.country).filter((c) => c && c !== p.country)} onPick={(c) => setPlace(i, { country: c })} label={p.country || "Pick country"} testId={i ? `add-contact-country-${i}` : "add-contact-country"}
            className={p.country ? "bg-amber-400/15 text-amber-200 hover:bg-amber-400/25 border border-amber-400/40" : "bg-white/10 text-slate-100 hover:bg-white/15"} />
          <Input placeholder="City (optional)" value={p.city} onChange={(e) => setPlace(i, { city: e.target.value })} className="flex-1" data-testid={i ? `add-contact-city-${i}` : "add-contact-city"} />
          {places.length > 1 && <button type="button" onClick={() => setPlaces(places.filter((_, j) => j !== i))} className="p-1.5 text-slate-500 hover:text-rose-300" aria-label="Remove place" data-testid={`add-contact-remove-place-${i}`}><X size={14} /></button>}
        </div>
      ))}
      {places.length < 6 && <button type="button" onClick={() => setPlaces([...places, newPlace()])} className="col-span-2 justify-self-start flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200" data-testid="add-contact-add-place"><Plus size={12} /> Add another place</button>}
      <Input placeholder="Name" {...field("name")} data-testid="add-contact-name" />
      <Input placeholder="Professions, e.g. Influencer, Celebrity" {...field("profession")} data-testid="add-contact-profession" />
      <Input placeholder="Phone" type="tel" inputMode="tel" {...field("phone")} data-testid="add-contact-phone" />
      <Input placeholder="WhatsApp (if different)" type="tel" inputMode="tel" {...field("whatsapp")} data-testid="add-contact-whatsapp" />
      <Input placeholder="Email" type="email" inputMode="email" {...field("email")} data-testid="add-contact-email" />
      <Input placeholder="Instagram @handle" autoCapitalize="none" {...field("instagram")} data-testid="add-contact-instagram" />
      <Input placeholder="TikTok @handle" autoCapitalize="none" {...field("tiktok")} data-testid="add-contact-tiktok" />
      <Input placeholder="LinkedIn profile link" autoCapitalize="none" {...field("linkedin")} className="col-span-2" data-testid="add-contact-linkedin" />
      <Input placeholder="How they can help (e.g. host in Lima)" {...field("notes")} className="col-span-2" data-testid="add-contact-notes" />
      <Button type="submit" disabled={busy} className="col-span-2 bg-emerald-400 text-[#04120c] hover:bg-emerald-300" data-testid="add-contact-submit"><UserPlus size={14} /> Save travel friend</Button>
      <PrivateNote className="col-span-2" testId="add-contact-private-note" />
      <p className="col-span-2 text-[11px] text-slate-500">When you date a trip or share your city there, we'll remind you who you know.</p>
    </form>
  );
};

const InPerson = ({ onDone }) => {
  const [scan, setScan] = useState(false);
  return (
    <div className="mt-3 space-y-4" data-testid="swap-in-person">
      <div className="grid grid-cols-2 gap-1 rounded-full bg-white/5 p-1 text-sm">
        <button type="button" onClick={() => setScan(false)} className={`rounded-full py-1.5 transition-colors ${!scan ? "bg-emerald-400 text-[#06121c] font-semibold" : "text-slate-300"}`} data-testid="swap-mode-show">Show my code</button>
        <button type="button" onClick={() => setScan(true)} className={`rounded-full py-1.5 transition-colors ${scan ? "bg-emerald-400 text-[#06121c] font-semibold" : "text-slate-300"}`} data-testid="swap-mode-scan">Scan their code</button>
      </div>
      {scan ? <ScanCode onDone={onDone} /> : <><MyCode /><NfcSticker /><PrintCard /></>}
    </div>
  );
};

export const AddTravelFriend = ({ open, onOpenChange, names, tab = "contact" }) => {
  const navigate = useNavigate();
  return (
    <DockSheet open={open} onOpenChange={onOpenChange} title="Add travel friend" description="Save someone you know abroad, swap codes with someone you just met, or add a friend who uses the bucketlist." testId="add-travel-friend-sheet">
      <Tabs key={tab} defaultValue={tab} className="mt-4 flex-1 overflow-y-auto">
        <TabsList className="grid grid-cols-4 bg-white/5 [&>button]:px-1 [&>button]:text-xs sm:[&>button]:text-sm">
          <TabsTrigger value="contact" data-testid="add-tab-contact">New</TabsTrigger>
          <TabsTrigger value="phone" data-testid="add-tab-phone">Phone book</TabsTrigger>
          <TabsTrigger value="swap" data-testid="add-tab-swap">In person</TabsTrigger>
          <TabsTrigger value="friend" data-testid="add-tab-friend">On the app</TabsTrigger>
        </TabsList>
        <TabsContent value="contact"><ContactForm names={names} onDone={() => onOpenChange(false)} /></TabsContent>
        <TabsContent value="phone"><PhoneBookImport names={names} onDone={() => onOpenChange(false)} /></TabsContent>
        <TabsContent value="swap"><InPerson onDone={() => onOpenChange(false)} /></TabsContent>
        <TabsContent value="friend" className="[&>div]:mt-3"><PeopleFinder onView={(id) => navigate(`/friend/${id}`)} /></TabsContent>
      </Tabs>
    </DockSheet>
  );
};
